import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sparkles, Scissors, Loader2, Download, Link2, Trash2, Globe, Clapperboard, RefreshCw, Wand2, Crosshair,
} from "lucide-react";

const ASPECTS = [
  { id: "9:16", label: "9:16 · Reels/TikTok" },
  { id: "1:1", label: "1:1 · Feed" },
  { id: "16:9", label: "16:9 · Landscape" },
];
const LENGTHS = [15, 20, 30, 45];

function fmt(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function StatusBadge({ status }) {
  const map = {
    queued: ["Queued", "bg-amber-500/15 text-amber-500 border-amber-500/30"],
    processing: ["Rendering", "bg-sky-500/15 text-sky-400 border-sky-500/30"],
    ready: ["Ready", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
    failed: ["Failed", "bg-rose-500/15 text-rose-500 border-rose-500/30"],
  };
  const [txt, cls] = map[status] || ["—", "bg-muted text-muted-foreground"];
  return <Badge variant="outline" className={`rounded-sm text-[10px] font-mono uppercase tracking-wider ${cls}`} data-testid="clip-status-badge">{txt}</Badge>;
}

const ASPECT_NUM = { "9:16": [9, 16], "1:1": [1, 1], "16:9": [16, 9] };

function CropPreview({ assetId, atSec, aspect, smart }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/assets/${assetId}/clips/crop-preview`, { at_sec: atSec, aspect, smart_crop: smart });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };
  const [rw, rh] = ASPECT_NUM[aspect] || [9, 16];
  const noReframe = data && data.reframes === false;
  return (
    <div className="rounded-sm border border-border bg-background/50 p-3" data-testid="clip-crop-preview">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
          <Crosshair className="w-3 h-3 text-sky-400" /> Smart-crop preview @ {fmt(atSec)}
        </Label>
        <Button size="sm" variant="outline" onClick={run} disabled={loading} className="rounded-sm text-[11px] border-border h-7" data-testid="clip-preview-btn">
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} {data ? "Refresh" : "Preview framing"}
        </Button>
      </div>
      {!data && <div className="text-[11px] text-muted-foreground">See exactly how {smart ? "the subject-tracked" : "the"} crop will frame this moment before you render.</div>}
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-mono text-muted-foreground mb-1">Before · source {data.img_w}×{data.img_h}</div>
            <div className="relative rounded-sm overflow-hidden border border-border" style={{ aspectRatio: `${data.img_w}/${data.img_h}` }}>
              <img src={data.image} alt="source frame" className="w-full h-full object-cover" />
              {!noReframe && (
                <div className="absolute border-2 border-fuchsia-400 pointer-events-none" data-testid="clip-preview-box"
                  style={{ left: `${data.rect.x * 100}%`, top: `${data.rect.y * 100}%`, width: `${data.rect.w * 100}%`, height: `${data.rect.h * 100}%`, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }} />
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono text-muted-foreground mb-1" data-testid="clip-preview-after-label">
              After · {aspect} {noReframe ? "· no reframe" : data.subject === "face" ? "· tracking face 🎯" : "· centred"}
            </div>
            <div className="mx-auto rounded-sm border border-fuchsia-500/40" data-testid="clip-preview-after"
              style={{
                aspectRatio: `${rw}/${rh}`, maxHeight: 220,
                backgroundImage: `url(${data.image})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: `${100 / data.rect.w}% ${100 / data.rect.h}%`,
                backgroundPosition: `${data.rect.w < 1 ? (data.rect.x / (1 - data.rect.w)) * 100 : 0}% ${data.rect.h < 1 ? (data.rect.y / (1 - data.rect.h)) * 100 : 0}%`,
              }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialClipsDialog({ asset, open, onClose }) {
  const [aspect, setAspect] = useState("9:16");
  const [clipLen, setClipLen] = useState(20);
  const [sugStatus, setSugStatus] = useState("none");
  const [suggestions, setSuggestions] = useState([]);
  const [clips, setClips] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [manual, setManual] = useState({ start_sec: 0, end_sec: 20, title: "", caption: "" });
  const [branding, setBranding] = useState(true);
  const [smartCrop, setSmartCrop] = useState(true);
  const pollRef = useRef(null);

  const dur = asset?.effective_duration_sec ?? asset?.duration_sec ?? 0;

  const loadSuggestions = useCallback(async () => {
    if (!asset) return;
    try {
      const r = await api.get(`/assets/${asset.id}/clips/suggestions`);
      setSugStatus(r.data.status);
      setSuggestions(r.data.suggestions || []);
      if (r.data.status !== "analyzing") setAnalyzing(false);
    } catch { /* noop */ }
  }, [asset]);

  const loadClips = useCallback(async () => {
    if (!asset) return;
    try {
      const r = await api.get(`/assets/${asset.id}/clips`);
      setClips(r.data.items || []);
    } catch { /* noop */ }
  }, [asset]);

  useEffect(() => {
    if (open && asset) {
      setManual({ start_sec: 0, end_sec: Math.min(20, Math.round(dur) || 20), title: "", caption: "" });
      loadSuggestions();
      loadClips();
    }
    return () => clearInterval(pollRef.current);
  }, [open, asset, loadSuggestions, loadClips, dur]);

  // Poll while anything is in flight.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!open) return;
    const busy = analyzing || sugStatus === "analyzing" || clips.some((c) => c.status === "queued" || c.status === "processing");
    if (busy) {
      pollRef.current = setInterval(() => { loadSuggestions(); loadClips(); }, 3500);
    }
    return () => clearInterval(pollRef.current);
  }, [open, analyzing, sugStatus, clips, loadSuggestions, loadClips]);

  const runAnalyze = async () => {
    setAnalyzing(true);
    setSugStatus("analyzing");
    try {
      await api.post(`/assets/${asset.id}/clips/analyze`, { clip_len_sec: clipLen });
      toast.success("Scanning for highlights…");
    } catch (e) {
      setAnalyzing(false);
      toast.error(e?.response?.data?.detail || "Analysis failed to start");
    }
  };

  const createClip = async (body) => {
    setCreating(true);
    try {
      await api.post(`/assets/${asset.id}/clips`, { ...body, aspect, branding, smart_crop: smartCrop });
      toast.success("Clip queued — rendering…");
      loadClips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create clip");
    }
    setCreating(false);
  };

  const togglePublic = async (clip) => {
    try {
      const r = await api.patch(`/clips/${clip.id}`, { public: !clip.public });
      setClips((p) => p.map((c) => (c.id === clip.id ? r.data : c)));
    } catch { toast.error("Update failed"); }
  };

  const removeClip = async (clip) => {
    if (!window.confirm("Delete this clip? This removes the file from storage.")) return;
    try {
      await api.delete(`/clips/${clip.id}`);
      setClips((p) => p.filter((c) => c.id !== clip.id));
      toast.success("Clip deleted");
    } catch { toast.error("Delete failed"); }
  };

  const copyLink = async (clip) => {
    const url = `${window.location.origin}/api/public/clip/${clip.id}/share`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="social-clips-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Clapperboard className="w-4 h-4 text-fuchsia-400" /> Social Clips — {asset.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Cut short, branded clips for TikTok / Reels / Shorts. AI finds the best moments, or set your own in/out.
          </DialogDescription>
        </DialogHeader>

        {/* Shared config */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-sm border border-border bg-background/50">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Aspect</Label>
            <div className="mt-1 flex flex-wrap gap-1">
              {ASPECTS.map((a) => (
                <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`clip-aspect-${a.id.replace(":", "x")}`}
                  className={`px-2 py-1 rounded-sm text-[11px] font-mono border transition-colors ${aspect === a.id ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {a.id}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">AI clip length</Label>
            <div className="mt-1 flex flex-wrap gap-1">
              {LENGTHS.map((l) => (
                <button key={l} onClick={() => setClipLen(l)} data-testid={`clip-len-${l}`}
                  className={`px-2 py-1 rounded-sm text-[11px] font-mono border transition-colors ${clipLen === l ? "border-sky-500/60 bg-sky-500/10 text-sky-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {l}s
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end justify-between gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Branding</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Switch checked={branding} onCheckedChange={setBranding} data-testid="clip-branding-toggle" />
                <span className="text-xs text-muted-foreground">Logo + caption burn-in</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Switch checked={smartCrop} onCheckedChange={setSmartCrop} data-testid="clip-smartcrop-toggle" />
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Crosshair className="w-3 h-3 text-sky-400" /> Smart crop (track subject)</span>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="ai" className="mt-2">
          <TabsList className="rounded-sm">
            <TabsTrigger value="ai" className="rounded-sm text-xs" data-testid="clip-tab-ai"><Sparkles className="w-3 h-3 mr-1" /> AI Highlights</TabsTrigger>
            <TabsTrigger value="manual" className="rounded-sm text-xs" data-testid="clip-tab-manual"><Scissors className="w-3 h-3 mr-1" /> Manual</TabsTrigger>
            <TabsTrigger value="clips" className="rounded-sm text-xs" data-testid="clip-tab-list"><Clapperboard className="w-3 h-3 mr-1" /> My Clips ({clips.length})</TabsTrigger>
          </TabsList>

          {/* AI */}
          <TabsContent value="ai" className="space-y-3 pt-3">
            <Button onClick={runAnalyze} disabled={analyzing || sugStatus === "analyzing"} className="rounded-sm tenant-btn text-xs" data-testid="clip-analyze-btn">
              {analyzing || sugStatus === "analyzing" ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Scanning…</> : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Detect highlights</>}
            </Button>
            {sugStatus === "failed" && <div className="text-xs text-rose-500" data-testid="clip-analyze-error">Analysis failed. Try again.</div>}
            {sugStatus === "ready" && suggestions.length === 0 && <div className="text-xs text-muted-foreground">No standout moments found — try the Manual tab.</div>}
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <div key={i} className="panel rounded-sm p-3 flex items-start gap-3" data-testid={`clip-suggestion-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{s.title}</div>
                    {s.caption && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.caption}</div>}
                    <div className="text-[10px] font-mono text-sky-400/80 mt-1">{fmt(s.start_sec)}–{fmt(s.end_sec)} · {Math.round(s.end_sec - s.start_sec)}s</div>
                  </div>
                  <Button size="sm" disabled={creating} onClick={() => createClip({ start_sec: s.start_sec, end_sec: s.end_sec, title: s.title, caption: s.caption })}
                    className="rounded-sm tenant-btn text-xs shrink-0" data-testid={`clip-create-suggestion-${i}`}>
                    <Scissors className="w-3 h-3 mr-1" /> Create
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Manual */}
          <TabsContent value="manual" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start (sec)</Label>
                <Input type="number" min={0} max={dur} step="0.1" value={manual.start_sec}
                  onChange={(e) => setManual((m) => ({ ...m, start_sec: parseFloat(e.target.value) || 0 }))}
                  className="rounded-sm mt-1 font-mono text-xs" data-testid="clip-manual-start" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">End (sec)</Label>
                <Input type="number" min={0} max={dur} step="0.1" value={manual.end_sec}
                  onChange={(e) => setManual((m) => ({ ...m, end_sec: parseFloat(e.target.value) || 0 }))}
                  className="rounded-sm mt-1 font-mono text-xs" data-testid="clip-manual-end" />
              </div>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground">Source duration: {fmt(dur)} · clip length {Math.max(0, Math.round((manual.end_sec - manual.start_sec) * 10) / 10)}s</div>
            <CropPreview assetId={asset.id} atSec={manual.start_sec} aspect={aspect} smart={smartCrop} />
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title</Label>
              <Input value={manual.title} onChange={(e) => setManual((m) => ({ ...m, title: e.target.value }))}
                placeholder={asset.name} className="rounded-sm mt-1 text-xs" data-testid="clip-manual-title" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Caption (burned in)</Label>
              <Textarea value={manual.caption} onChange={(e) => setManual((m) => ({ ...m, caption: e.target.value }))}
                placeholder="Optional on-screen caption" rows={2} className="rounded-sm mt-1 text-xs" data-testid="clip-manual-caption" />
            </div>
            <Button disabled={creating || manual.end_sec <= manual.start_sec} onClick={() => createClip(manual)}
              className="rounded-sm tenant-btn text-xs" data-testid="clip-manual-create">
              {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5 mr-1.5" />} Create clip
            </Button>
          </TabsContent>

          {/* Clips list */}
          <TabsContent value="clips" className="space-y-2 pt-3">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={loadClips} className="rounded-sm text-xs border-border" data-testid="clip-refresh-list">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
            {clips.length === 0 && <div className="text-xs text-muted-foreground py-6 text-center">No clips yet. Create one from AI Highlights or Manual.</div>}
            {clips.map((c) => (
              <div key={c.id} className="panel rounded-sm p-3 flex items-center gap-3" data-testid={`clip-row-${c.id}`}>
                <div className="w-16 h-16 rounded-sm bg-black/40 overflow-hidden shrink-0 flex items-center justify-center">
                  {c.poster_url ? <img src={c.poster_url} alt="" className="w-full h-full object-cover" /> : <Clapperboard className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-sm truncate">{c.title}</span>
                    <StatusBadge status={c.status} />
                    <span className="text-[10px] font-mono text-muted-foreground">{c.aspect} · {fmt(c.duration_sec)}</span>
                  </div>
                  {c.caption && <div className="text-xs text-muted-foreground truncate mt-0.5">{c.caption}</div>}
                  {c.status === "failed" && <div className="text-[11px] text-rose-500 mt-0.5 truncate">{c.error}</div>}
                  {c.status === "ready" && (
                    <div className="flex items-center gap-3 mt-1.5">
                      <a href={c.video_url} download target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono text-sky-400 hover:text-sky-300 inline-flex items-center gap-1" data-testid={`clip-download-${c.id}`}><Download className="w-3 h-3" /> Download</a>
                      <button onClick={() => copyLink(c)} className="text-[11px] font-mono text-fuchsia-400 hover:text-fuchsia-300 inline-flex items-center gap-1" data-testid={`clip-copylink-${c.id}`}><Link2 className="w-3 h-3" /> Share link</button>
                      <label className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer" title="Make the share link public">
                        <Globe className="w-3 h-3" /> Public
                        <Switch checked={!!c.public} onCheckedChange={() => togglePublic(c)} data-testid={`clip-public-${c.id}`} />
                      </label>
                    </div>
                  )}
                  {(c.status === "queued" || c.status === "processing") && (
                    <div className="text-[11px] font-mono text-sky-400/80 mt-1 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {c.status === "queued" ? "Waiting…" : "Rendering…"}</div>
                  )}
                </div>
                <button onClick={() => removeClip(c)} className="text-muted-foreground hover:text-rose-500 shrink-0" data-testid={`clip-delete-${c.id}`} title="Delete clip">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
